'use client';

import type { UiState } from 'instantsearch.js';
import urlJoin from 'url-join';

import { ORDERED_SEO_FRIENDLY_FACET_FILTERS } from '@libs/algolia';

import { FACET_VALUE_SEPARATOR, FACETS_KEY, URL_ENCODED } from '@/constants/facetedNavigation';
import { SEO_FACET_URL_NAMES } from '@/constants/seoFacets';

type FilterValue = string[] | undefined;

const SEO_FACET_ATTRIBUTES = new Set(Object.keys(ORDERED_SEO_FRIENDLY_FACET_FILTERS));

function getSeoFriendlyFacetKeys() {
    return [...SEO_FACET_ATTRIBUTES];
}

function getFacetValuesForSeo(routeState: UiState, attribute: string): FilterValue {
    const urlName = ORDERED_SEO_FRIENDLY_FACET_FILTERS[attribute];
    const directValues = routeState[attribute] as FilterValue;
    if (directValues?.length) {
        return directValues;
    }

    if (!urlName) {
        return undefined;
    }

    const urlNameKey = Object.keys(routeState).find(key => key.toLowerCase() === urlName.toLowerCase());

    return urlNameKey ? (routeState[urlNameKey] as FilterValue) : undefined;
}

const formatFacetValues = (values: Array<string | boolean>) =>
    values
        .map(value => encodeURIComponent(String(value).toLowerCase()).replaceAll("'", URL_ENCODED.APOSTROPHE))
        .filter(value => value.length > 0);

const generateSeoFriendlyFacetPath = (facets: UiState) => {
    const facetsCollection: string[] = [];

    getSeoFriendlyFacetKeys().forEach(attribute => {
        const facetValues = getFacetValuesForSeo(facets, attribute);
        if (facetValues?.length) {
            const formattedValues = formatFacetValues(facetValues);
            if (formattedValues.length > 0) {
                facetsCollection.push(
                    ORDERED_SEO_FRIENDLY_FACET_FILTERS[attribute],
                    formattedValues.toSorted((a, b) => a.localeCompare(b)).join(FACET_VALUE_SEPARATOR)
                );
            }
        }
    });

    return facetsCollection.join('/').toLowerCase();
};

export const mapFacetValuesToUrl = (facets: UiState, location: Location) => {
    const slugs = location.pathname.split('/');
    const facetsIndex = slugs?.indexOf(FACETS_KEY);
    const hasFacets = facetsIndex >= 0;

    if (hasFacets) {
        slugs.splice(facetsIndex);
    }

    const seoFacetsPath = generateSeoFriendlyFacetPath(facets);
    const urlFacetKey = seoFacetsPath.length ? FACETS_KEY : '';

    return urlJoin('/', ...slugs, urlFacetKey, seoFacetsPath);
};

export const filterOutSeoFriendlyFacets = (facets: UiState) => {
    const seoFriendlyFacetKeys = getSeoFriendlyFacetKeys();
    const filteredFacets: Record<string, FilterValue> = {};

    Object.keys(facets).forEach(key => {
        const isSeoAttribute = seoFriendlyFacetKeys.includes(key);
        const isSeoUrlName = SEO_FACET_URL_NAMES.has(key.toLowerCase());

        if (!isSeoAttribute && !isSeoUrlName && facets[key]) {
            filteredFacets[key] = facets[key] as FilterValue;
        }
    });

    return filteredFacets as UiState;
};
